/**
 * Coordinador principal del sistema de idle global
 * Orquesta activity detector, bus y leader election
 */

import { idleConfig } from './config';
import { getBus, destroyBus } from './bus';
import { getActivityDetector, destroyActivityDetector } from './activity';
import { WebLocksLeader } from './leader/WebLocksLeader';
import { StorageLeader } from './leader/StorageLeader';
import { IdleCoordinatorOptions, StopHandle } from './types';
import { MESSAGE_TYPES, LOGOUT_REASONS } from './constants';

class IdleCoordinator {
  private isRunning = false;
  private leader: WebLocksLeader | StorageLeader | null = null;
  private unsubscribeBus: (() => void) | null = null;
  private options: IdleCoordinatorOptions | null = null;

  async start(options: IdleCoordinatorOptions): Promise<StopHandle> {
    if (this.isRunning) {
      return { stop: () => this.stop() };
    }

    if (!idleConfig.enabled) {
      return { stop: () => {} };
    }

    this.options = options;
    this.isRunning = true;

    try {
      // 1. Configurar listener para mensajes de logout
      this.setupLogoutListener();

      // 2. Elegir e iniciar estrategia de liderazgo PRIMERO
      await this.startLeaderStrategy();

      // 3. AHORA iniciar detector de actividad (para que el leader ya esté escuchando)
      const activityDetector = getActivityDetector();
      activityDetector.start();
      return {
        stop: () => this.stop(),
      };
    } catch (error) {
      this.stop();
      throw error;
    }
  }

  stop(): void {
    if (!this.isRunning) {
      return;
    }

    this.isRunning = false;

    // Detener leader
    if (this.leader) {
      this.leader.stop();
      this.leader = null;
    }

    // Desuscribirse del bus
    if (this.unsubscribeBus) {
      this.unsubscribeBus();
      this.unsubscribeBus = null;
    }

    // Detener detector de actividad
    destroyActivityDetector();

    // Destruir bus
    destroyBus();

    this.options = null;
  }

  private setupLogoutListener(): void {
    const bus = getBus();
    this.unsubscribeBus = bus.subscribe((message) => {
      if (message.type === MESSAGE_TYPES.LOGOUT) {
        this.handleLogoutMessage(message.reason);
      }
    });
  }

  private handleLogoutMessage(reason?: string): void {
    if (!this.isRunning || !this.options) return;

    try {
      // Ejecutar logout local
      this.options.logout(reason || LOGOUT_REASONS.LEADER_COMMAND);
    } catch (error) {
      console.error('Error executing logout callback:', error);
    }
  }

  private async startLeaderStrategy(): Promise<void> {
    if (!this.options) {
      throw new Error('Options not set');
    }

    // Asegurar que el bus esté disponible PRIMERO (se inicializa automáticamente)
    getBus();

    // Decidir estrategia basada en soporte de Web Locks
    if (WebLocksLeader.isSupported()) {
      this.leader = new WebLocksLeader(this.options.logout);
    } else {
      this.leader = new StorageLeader(this.options.logout);
    }

    await this.leader.start();
  }

  // Método para verificar si el sistema está activo
  isActive(): boolean {
    return this.isRunning;
  }

  // Método para obtener información de estado (útil para debugging)
  getStatus(): {
    isRunning: boolean;
    isEnabled: boolean;
    leaderType: string | null;
    lastActivity: number;
  } {
    const activityDetector = getActivityDetector();
    
    return {
      isRunning: this.isRunning,
      isEnabled: idleConfig.enabled,
      leaderType: this.leader instanceof WebLocksLeader ? 'WebLocks' : 
                  this.leader instanceof StorageLeader ? 'Storage' : null,
      lastActivity: this.isRunning ? activityDetector.getLastActivity() : 0,
    };
  }
}

// Singleton instance
let coordinatorInstance: IdleCoordinator | null = null;

export const getIdleCoordinator = (): IdleCoordinator => {
  if (!coordinatorInstance) {
    coordinatorInstance = new IdleCoordinator();
  }
  return coordinatorInstance;
};

export const destroyIdleCoordinator = (): void => {
  if (coordinatorInstance) {
    coordinatorInstance.stop();
    coordinatorInstance = null;
  }
};

// API principal para uso externo
export const startIdleGlobal = async (options: IdleCoordinatorOptions): Promise<StopHandle> => {
  // Guard adicional: verificar si ya hay una instancia corriendo
  if (coordinatorInstance?.isActive()) {
    return { stop: () => stopIdleGlobal() };
  }

  const coordinator = getIdleCoordinator();
  return coordinator.start(options);
};

export const stopIdleGlobal = (): void => {
  destroyIdleCoordinator();
};

export const getIdleGlobalStatus = () => {
  const coordinator = getIdleCoordinator();
  return coordinator.getStatus();
};
